import { useEffect, useState } from "react";
import api, { formatINR, formatDate } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Download, FileText, Printer } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "quarter", label: "Last 90 days" },
  { key: "year", label: "This Year" },
];

const COLORS = ["#2563EB", "#10B981", "#F97316", "#8B5CF6", "#EF4444"];

const getRange = (key) => {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const start = new Date(now); const end = new Date(now);
  if (key === "today") return { start: iso(start), end: iso(end) };
  if (key === "yesterday") { start.setDate(start.getDate() - 1); end.setDate(end.getDate() - 1); return { start: iso(start), end: iso(end) }; }
  if (key === "week") { start.setDate(start.getDate() - 6); return { start: iso(start), end: iso(end) }; }
  if (key === "month") { start.setDate(1); return { start: iso(start), end: iso(end) }; }
  if (key === "quarter") { start.setDate(start.getDate() - 89); return { start: iso(start), end: iso(end) }; }
  if (key === "year") { start.setMonth(0, 1); return { start: iso(start), end: iso(end) }; }
  return { start: iso(start), end: iso(end) };
};

const Reports = () => {
  const [range, setRange] = useState("month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { start, end } = range === "custom" ? custom : getRange(range);
    if (!start || !end) { setLoading(false); return; }
    const { data } = await api.get(`/reports/sales?start=${start}&end=${end}`);
    setData(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  const exportCSV = () => {
    if (!data) return;
    const headers = ["Bill No", "Date", "Customer", "Mobile", "Status", "Payment", "Grand Total"];
    const rows = data.bills.map((b) => [b.bill_number, formatDate(b.created_at), b.customer_name, b.mobile, b.status, b.payment_mode, b.grand_total]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `quickwash-sales-${data.start}-${data.end}.csv`;
    a.click();
    toast.success("CSV downloaded");
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>Sales Reports</h1>
        <p className="text-sm text-slate-500">Comprehensive breakdown of revenue, orders and payment modes.</p>
      </div>

      <div className="qw-dash-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} className={`px-3 py-1.5 rounded-full text-sm transition-colors ${range === r.key ? 'bg-blue-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`} data-testid={`range-${r.key}`}>{r.label}</button>
          ))}
          <button onClick={() => setRange("custom")} className={`px-3 py-1.5 rounded-full text-sm transition-colors ${range === "custom" ? 'bg-blue-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`} data-testid="range-custom">Custom</button>
        </div>
        {range === "custom" && (
          <div className="flex gap-2 items-center">
            <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="qw-input py-1.5" data-testid="custom-start"/>
            <span className="text-slate-400">to</span>
            <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="qw-input py-1.5" data-testid="custom-end"/>
            <button onClick={load} className="qw-btn-primary py-1.5 px-4">Apply</button>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={exportCSV} disabled={!data} className="qw-btn-outline" data-testid="export-csv"><Download size={16}/>CSV</button>
          <button onClick={() => window.print()} className="qw-btn-outline" data-testid="print-report"><Printer size={16}/>Print</button>
        </div>
      </div>

      {loading || !data ? <div className="h-40 qw-dash-card animate-pulse"/> : (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="qw-dash-card p-6"><div className="text-xs uppercase text-slate-500 font-semibold">Total Revenue</div><div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Poppins' }}>{formatINR(data.total_revenue)}</div></div>
            <div className="qw-dash-card p-6"><div className="text-xs uppercase text-slate-500 font-semibold">Total Orders</div><div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Poppins' }}>{data.total_orders}</div></div>
            <div className="qw-dash-card p-6"><div className="text-xs uppercase text-slate-500 font-semibold">Total Discount</div><div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Poppins' }}>{formatINR(data.total_discount)}</div></div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="qw-dash-card p-6 lg:col-span-2">
              <div className="font-semibold mb-3" style={{ fontFamily: 'Poppins' }}>Daily Revenue</div>
              {data.daily.length === 0 ? <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No sales in this range</div> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.daily}><CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false}/><XAxis dataKey="date" stroke="#94A3B8" fontSize={11}/><YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `₹${v}`}/><Tooltip formatter={(v) => formatINR(v)}/><Bar dataKey="amount" fill="#2563EB" radius={[6, 6, 0, 0]}/></BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="qw-dash-card p-6">
              <div className="font-semibold mb-3" style={{ fontFamily: 'Poppins' }}>Payment Modes</div>
              {data.by_payment.length === 0 ? <div className="h-64 flex items-center justify-center text-slate-400 text-sm">No data</div> : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={data.by_payment} dataKey="amount" nameKey="mode" innerRadius={50} outerRadius={90}>
                      {data.by_payment.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                    </Pie><Tooltip formatter={(v) => formatINR(v)}/><Legend wrapperStyle={{ fontSize: 12 }}/>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="qw-dash-card overflow-x-auto">
            <table className="w-full text-sm"><thead className="bg-slate-50 border-b text-xs uppercase text-slate-500"><tr><th className="text-left p-4">Bill</th><th className="text-left p-4">Date</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Payment</th><th className="text-right p-4">Amount</th></tr></thead>
              <tbody>{data.bills.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-slate-400">No bills in this range</td></tr> :
                data.bills.slice(0, 50).map((b) => (
                  <tr key={b.id} className="border-b border-slate-100"><td className="p-4 font-semibold">{b.bill_number}</td><td className="p-4">{formatDate(b.created_at)}</td><td className="p-4">{b.customer_name}</td><td className="p-4"><span className="qw-badge-slate">{b.payment_mode}</span></td><td className="p-4 text-right font-semibold">{formatINR(b.grand_total)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
